/* eslint-disable max-depth */
import { usePushdownContext } from '../../../../../../../../providers/Pushdown/PushdownProvider';
import { ISubmitResponse } from '../../../../../../../../types/shared/PrequalificationDataEntry';
import SessionStorage from '../../../../../../../../utils/SessionStorage';
import {
    AcquisitionFlow,
    callDataExposureApi,
    clearSessionStorageVariablesForAcquisitionFlow, 
    getDataEntryPages,
    withQueryParams,
} from '../../../../../../../../utils/acquisition-utils';
import {
    flowDataModule,
} from '../../../../../../../../utils/flowDataModule';
import { useLoader } from '../../../../../../providers/LoaderProvider';
import { usePageProperties } from '../../../../../../providers/PagePropertiesProvider';
import { usePersonalInformation } from '../../../../../../providers/PersonalInformationProvider';
import setCardInfo from '../util/setCardInfo';
import setSelectedProgramCode from '../util/setSelectedProgramCode';
import useGetDynamicContent from './useGetDynamicContent';
import { useHomeAddress } from '../../../../../../providers/HomeAddressProvider';
import axios from 'axios';
import { TApplicationInformation } from '../../../../../../../../types/models/OfferType';
import { getParameterByName } from '../../../../../../../../utils/cob-utils';

async function getMultiOfferContent(ApplicationInformation: TApplicationInformation) {
    const programCodes = ApplicationInformation.map(
        (offer: any) => offer.ProgramRates.ProgramCode,
    ).join(',');

    const partnerId = getParameterByName('C1BPartnerID') || '';

    const output: string[] = [];
    if (programCodes || partnerId) {
        output.push('?');
    }
    if (programCodes) {
        output.push(`programCodes=${programCodes}`);
    }
    if (programCodes && partnerId) {
        output.push('&');
    }
    if (partnerId) {
        output.push(`partnerId=${partnerId}`);
    }

    const params = output.join('');

    await axios
        .get(`/services/multioffer${params}`)
        .then(({ data }) => {
            SessionStorage.setObject('multiOfferContent', data);
        })
        .then(async () => await callDataExposureApi(AcquisitionFlow.PQ))
        .catch(() => {
            const queryParams = new URLSearchParams(window.location.search);
            queryParams.set('errorCode', 'noTncError');
            const { preQualDataEntryPageUrl } = getDataEntryPages();
            window.location.assign(`${preQualDataEntryPageUrl}?${queryParams.toString()}`);
        });

    // This is returned in legacy, a better approach might be to return a Promise.reject and Promise.resolve to handle next steps
    return 'done!';
}

export default function usePrequalApproved() {
    const {
        fullAppRedirectUrl,
        multiOfferRedirectUrl,
        counterOfferEsignConsentRedirectUrl,
    } = usePageProperties();
    const { personalInformation } = usePersonalInformation();
    const { homeAddress } = useHomeAddress();
    const { setShouldRenderLoader } = useLoader();
    const { showPushdown } = usePushdownContext();
    const { getDynamicContent } = useGetDynamicContent();

    const approvedFlow = async (data: ISubmitResponse) => {
        // save the personal info data in  SessionStorage to be used in PQ Application
        const {
            FirstName,
            MiddleInitial,
            LastName,
            EmailAddress,
            MailingAddressLine1,
            MailingAddressLine2,
            MailingCity,
            MailingState,
            MailingZipCode,
            PrimaryPhoneNumber,
            Ssn,
            dob,
        } = personalInformation;

        const {
            HomeAddressLine1,
            HomeAddressLine2,
            HomeZipCode,
            HomeState,
            HomeCity,
        } = homeAddress;

        const dobSplit = dob.split('/');

        const DOBDay = parseInt(dobSplit[1]);
        const DOBMonth = parseInt(dobSplit[0]);
        const DOBYear = parseInt(dobSplit[2]);

        await SessionStorage.setCipherWithCurrentScope(
            'personalInfo',
            {
                FirstName,
                MiddleInitial,
                LastName,
                EmailAddress,
                MailingAddressLine1,
                MailingAddressLine2,
                MailingCity,
                MailingState,
                MailingZipCode,
                IsMailingHomeAddressSame: !HomeAddressLine1 || MailingAddressLine1 === HomeAddressLine1,
                HomeAddressLine1,
                HomeAddressLine2,
                HomeZipCode,
                HomeState,
                HomeCity,
                PrimaryPhoneNumber,
                Ssn,
                DOBDay,
                DOBMonth,
                DOBYear,
            },
        );

        const { ApplicationInformation, AdditionalInformation } = data;

        await flowDataModule.setInSession({
            FirstName: FirstName,
            ZipCode: MailingZipCode,
            ...AdditionalInformation,
        });

        if (ApplicationInformation) {

            let offerType = 'noOffers';
            if (ApplicationInformation.length === 1) {
                offerType = 'singleOffer';
            } else if (ApplicationInformation.length > 1) {
                offerType = 'multiOffer';
            }

            if (offerType === 'multiOffer') {
                getMultiOfferContent(ApplicationInformation).then(() => {
                    window.location.assign(withQueryParams(multiOfferRedirectUrl));
                }).catch(() => {
                    clearSessionStorageVariablesForAcquisitionFlow();
                    window.location.assign(
                        withQueryParams(
                            getDataEntryPages().preQualDataEntryPageUrl + '?errorCode=processingError',
                        ),
                    );
                });
            }

            if (offerType === 'singleOffer') {
                const {
                    IsCounterOffer = '',
                    IsSecuredCard = '',
                    ProgramRates,
                } = ApplicationInformation[0];
                /**
                 * this may seem odd but it is in legacy at pre-qualification.js line 596-597
                 */
                SessionStorage.set('programNumber', '1');
                setCardInfo(data);
                await flowDataModule.updateInSession({
                    Cards: SessionStorage.getObject('cardInfo'),
                });

                setSelectedProgramCode(ProgramRates);

                SessionStorage.set('isCounterOffer', IsCounterOffer);

                if (ProgramRates?.ProgramCode) {
                    try {
                        await axios.get(`/services/appoffermodule?programCode=${ProgramRates.ProgramCode}`);
                    } catch {
                        const queryParams = new URLSearchParams(window.location.search);
                        queryParams.set('errorCode', 'noTncError');
                        const { preQualDataEntryPageUrl } = getDataEntryPages();
                        window.location.assign(`${preQualDataEntryPageUrl}?${queryParams.toString()}`);
                        return;
                    }
                }

                getDynamicContent(
                    SessionStorage.tryGet('selectedProgramCode', ''),
                )
                    .then(async () => await callDataExposureApi(AcquisitionFlow.PQ))
                    .then(() => {
                        if (IsCounterOffer) {
                            if (IsSecuredCard) {
                                let url = withQueryParams(counterOfferEsignConsentRedirectUrl);
                                url = `${url}${url.includes('?') ? '&' : '?'}SecuredCounter=true`;
                                window.location.assign(url);
                            }

                            return window.location.assign(
                                withQueryParams(
                                    counterOfferEsignConsentRedirectUrl,
                                ),
                            );
                        }

                        return window.location.assign(
                            withQueryParams(fullAppRedirectUrl),
                        );
                    });
            }

            if (offerType === 'noOffers') {
                setShouldRenderLoader(false);

                SessionStorage.remove('prequalResponse');

                showPushdown({
                    header: 'Problem Processing Your Request',
                    description:
                        'We\'re unable to process your pre-qualification application at this time. Please try again in a few minutes.',
                    color: 'error',
                });
            }
        }
    };

    return {
        approvedFlow,
    };
}
